import { Box, MessageBlock } from '@deckee/deck-hand';
import React, { FC, useCallback, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import Shimmer from '../../../../shared/Shimmer/Shimmer';
import WeatherDirectionChevron from '../charts/WeatherDirectionChevron';
import { type WeatherForecastDaySelectHandler } from '../../helpers/analytics/weatherForecastAnalytics';
import { splitForecastDaysForOutlook } from '../../helpers/forecastHorizon';
import { type WeatherForecastMoonPhaseDay } from '../../helpers/moon/formatMoonPhases';
import { type WeatherForecastMoonPhasesError } from '../../hooks/useWeatherForecastMoonPhases';
import MoonPhaseIcon from '../MoonPhaseIcon/MoonPhaseIcon';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';
import GraphCardHeader from '../GraphCards/GraphCardHeader';
import CollapsibleSection from '../CollapsibleSection/CollapsibleSection';

const DayRow = styled(Box)<{ $isSelected?: boolean; $clickable?: boolean }>`
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.primary : theme.colors.lightergrey};
  border-radius: 8px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const TimeColumn = styled(Box)`
  min-width: 60px;
  text-align: left;
`;

/** Row body text: phase name and rise/set times (Figma darkergrey, medium weight). */
const moonPhasePrimaryTextProps = {
  fontSize: 1,
  fontWeight: 500 as const,
  color: 'black' as const,
  lineHeight: '1.2' as const,
};

/** Row secondary text: "% full", "Set", "Rise" labels (Figma darkgrey). */
const moonPhaseSecondaryTextProps = {
  fontSize: 1,
  fontWeight: 400 as const,
  color: 'darkergrey' as const,
  lineHeight: '1.2' as const,
};

export interface MoonPhaseCardProps {
  moonPhaseDays: WeatherForecastMoonPhaseDay[];
  selectedDate: string;
  onSelectDay?: WeatherForecastDaySelectHandler;
  isLoading?: boolean;
  error?: WeatherForecastMoonPhasesError | null;
}

const formatMoonPhaseHeader = (
  moonPhaseDay: WeatherForecastMoonPhaseDay | undefined,
): { primary: string; secondary: string | null } => {
  if (!moonPhaseDay?.label) {
    return { primary: '—', secondary: null };
  }

  return {
    primary: moonPhaseDay.label,
    secondary: `${moonPhaseDay.percentageVisible}% full`,
  };
};

const MoonPhaseDayRow: FC<{
  moonPhaseDay: WeatherForecastMoonPhaseDay;
  isSelected: boolean;
  clickable: boolean;
  riseSetLabelColor: string;
  onSelectDay?: (date: string, dayLabel: string) => void;
}> = ({
  moonPhaseDay,
  isSelected,
  clickable,
  riseSetLabelColor,
  onSelectDay,
}) => {
  const handleClick = () => {
    onSelectDay?.(moonPhaseDay.date, moonPhaseDay.dayLabel);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <DayRow
      $isSelected={isSelected}
      $clickable={clickable}
      px={3}
      pt={3}
      pb={2.5}
      mb={2}
      onClick={clickable ? handleClick : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      data-testid={`weather-forecast-moon-phase-day-${moonPhaseDay.date}`}
      data-selected={isSelected ? 'true' : 'false'}
    >
      <Box fontSize={2} fontWeight="bold" color="black" mb={2.5}>
        {moonPhaseDay.dayLabel}
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box
          display="flex"
          alignItems="center"
          flex="1 1 auto"
          minWidth={0}
          mr={2}
        >
          <Box flex="0 0 30px" mr="10px">
            <MoonPhaseIcon
              hemisphere={moonPhaseDay.hemisphere ?? 'southern'}
              percentageVisible={moonPhaseDay.percentageVisible}
              code={moonPhaseDay.code}
            />
          </Box>

          <Box minWidth={0}>
            <Box {...moonPhasePrimaryTextProps}>{moonPhaseDay.label}</Box>
            <Box {...moonPhaseSecondaryTextProps}>
              {moonPhaseDay.percentageVisible}% full
            </Box>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" flex="0 0 auto">
          <TimeColumn>
            <Box {...moonPhasePrimaryTextProps}>
              {moonPhaseDay.riseTime ?? 'No rise'}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              mt="1"
            >
              <WeatherDirectionChevron
                shape="navigation"
                direction="up"
                color={riseSetLabelColor}
              />
              <Box as="span" {...moonPhaseSecondaryTextProps} ml="1">
                Rise
              </Box>
            </Box>
          </TimeColumn>

          <TimeColumn ml="3">
            <Box {...moonPhasePrimaryTextProps}>
              {moonPhaseDay.setTime ?? 'No set'}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              mt="1"
            >
              <WeatherDirectionChevron
                shape="navigation"
                direction="down"
                color={riseSetLabelColor}
              />
              <Box as="span" {...moonPhaseSecondaryTextProps} ml="1">
                Set
              </Box>
            </Box>
          </TimeColumn>
        </Box>
      </Box>
    </DayRow>
  );
};

const MoonPhaseCard: FC<MoonPhaseCardProps> = ({
  moonPhaseDays,
  selectedDate,
  onSelectDay,
  isLoading = false,
  error = null,
}) => {
  const theme = useTheme();
  const riseSetLabelColor = theme.colors.darkergrey;

  const handleSelectDay = useCallback(
    (date: string, dayLabel: string) => {
      onSelectDay?.(date, dayLabel, 'moon phase');
    },
    [onSelectDay],
  );
  const selectedMoonPhaseDay = useMemo(
    () => moonPhaseDays.find((day) => day.date === selectedDate),
    [moonPhaseDays, selectedDate],
  );
  const {
    nearTermDays: nearTermMoonPhaseDays,
    extendedDays: extendedMoonPhaseDays,
  } = splitForecastDaysForOutlook(moonPhaseDays);
  const header = useMemo(
    () => formatMoonPhaseHeader(selectedMoonPhaseDay),
    [selectedMoonPhaseDay],
  );

  if (isLoading) {
    return (
      <CollapsibleCard
        title="Moon phase"
        testId="weather-forecast-moon-phase-card"
        analyticsCard="moon phase"
      >
        <Shimmer on>
          <Box minHeight="280px" />
        </Shimmer>
      </CollapsibleCard>
    );
  }

  if (error) {
    return (
      <CollapsibleCard
        title="Moon phase"
        testId="weather-forecast-moon-phase-card"
        analyticsCard="moon phase"
      >
        <MessageBlock
          type={error.type === 'warning' ? 'warning' : 'error'}
          size="small"
        >
          {error.message}
        </MessageBlock>
      </CollapsibleCard>
    );
  }

  if (moonPhaseDays.length === 0) {
    return (
      <CollapsibleCard
        title="Moon phase"
        testId="weather-forecast-moon-phase-card"
        analyticsCard="moon phase"
      >
        <MessageBlock type="warning" size="small">
          No moon phase data available for this location.
        </MessageBlock>
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="Moon phase"
      testId="weather-forecast-moon-phase-card"
      analyticsCard="moon phase"
    >
      <GraphCardHeader primary={header.primary} secondary={header.secondary} />

      <Box>
        {nearTermMoonPhaseDays.map((moonPhaseDay) => (
          <MoonPhaseDayRow
            key={moonPhaseDay.date}
            moonPhaseDay={moonPhaseDay}
            isSelected={moonPhaseDay.date === selectedDate}
            clickable={Boolean(onSelectDay)}
            riseSetLabelColor={riseSetLabelColor}
            onSelectDay={handleSelectDay}
          />
        ))}
      </Box>

      {extendedMoonPhaseDays.length > 0 ? (
        <CollapsibleSection
          title="Extended moon phases"
          testId="weather-forecast-extended-moon-phases"
          analyticsCard="extended moon phases"
          defaultExpanded={false}
        >
          {extendedMoonPhaseDays.map((moonPhaseDay) => (
            <MoonPhaseDayRow
              key={moonPhaseDay.date}
              moonPhaseDay={moonPhaseDay}
              isSelected={moonPhaseDay.date === selectedDate}
              clickable={Boolean(onSelectDay)}
              riseSetLabelColor={riseSetLabelColor}
              onSelectDay={handleSelectDay}
            />
          ))}
        </CollapsibleSection>
      ) : null}
    </CollapsibleCard>
  );
};

export default MoonPhaseCard;
